/**
 * Advisor View Investor Experience Activity.
 * @module advisor/components/reports/AdvisorViewInvestorExperienceActivity
 *
 */

import React, {useState} from 'react';
import debug from 'debug';
import {connect} from 'react-redux';
import axiosInstance from '../../../shared/utils/axios';
import {_openModal} from '../../../shared/reducers/modals';
import moment from 'moment';
import lazy from 'lazy.js';
import {_getUnreadReports} from '../../../shared/store';
import PropTypes from 'prop-types';
import {DatePicker, DescriptiveText, Row, SectionTitle, Steps} from '../../../shared';
import Table from '../../../shared/components/Table';

const log = debug('circleblack:report:AdvisorViewInvestorExperienceActivity');

/**
 * Advisor View Investor Experience Activity component.
 *
 * @name AdvisorViewInvestorExperienceActivity
 * @requires module:shared/components/Button
 * @requires module:shared/components/Table
 * @requires module:shared/components/SectionTitle
 * @requires module:shared/components/Dropdown
 *
 */

const AdvisorViewInvestorExperienceActivity = props => {
  const firm = props.session.profile.firm;
  const [step, setStep] = useState(0);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const today = moment().format('YYYY-MM-DD');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const setupParams = report => {
    log('setupParams');
    return lazy(report.parameters).reject({name: 'format'}).indexBy('name').toObject();
  };

  const [params, setParams] = useState(setupParams(props.report));

  const canGenerate = () => params?.startDate?.value && params?.endDate?.value && firm.id;

  const generateReport = () => {
    if (moment(params.endDate.value, 'YYYY-MM-DD').isBefore(params.startDate.value, 'YYYY-MM-DD')) {
      return props.openModal({
        type: 'MessageModal',
        title: 'Invalid Start and End Date.',
        body: 'Cannot generate report for an end date before the start date.',
      });
    }

    const submitParam = {
      ...params,
      firmID: {...params.firmID, value: firm.id},
      agentIDs: selectedAgents === 'ALL' ? undefined : {
        ...params.agentIDs,
        value: selectedAgents.map((user) => Number(user.id)),
      },
      userIDs: selectedClients === 'ALL' ? undefined : {
        ...params.userIDs,
        value: selectedClients.map((user) => Number(user.id)),
      },
    };

    return axiosInstance.post(
      '/reports/' + props.report.name,
      {parameters: Object.values(submitParam).filter(p => !!p).map((param) => ({id: param.id, value: param.value}))}
    )
      .then(() => {
        setSuccessMessage('The report you requested is generating. You may view it shortly.');
        setStep('END');

        props.getUnreadReports(props.unreadReportsTimeoutID, props.unreadReports, props.reportPageVisitTimestamp, 30000);
      })
      .catch(err => {
        const title = err.response && err.response.data && err.response.data.error && err.response.data.error.title !== undefined
          ? err.response.data.error.title
          : 'Report Couldn\'t be created';
        const body = (err.response && err.response.data && err.response.data.error && err.response.data.error.msg) || err.message;
        setErrorMessage(title + ': ' + body);
      });
  };

  const agentDataManager = {
    gatherData: (inputValue) => axiosInstance.get(
      `/firms/${firm.id}/viewAsInvestor/Agents`,
      {params: {...inputValue}}
    )
      .then(res => res.data)
      .catch(function error(err) {
        log('agentDataManager error', err);
        throw err;
      }),
    setSelectedRows: rows => setSelectedAgents(rows),
  };

  const clientDataManager = {
    gatherData: (inputValue) => axiosInstance.get(
      `/firms/${firm.id}/viewAsInvestor/Users`,
      {params: {...inputValue}}
    )
      .then(res => res.data)
      .catch(function error(err) {
        log('clientDataManager error', err);
        throw err;
      }),
    setSelectedRows: rows => setSelectedClients(rows),
  };

  const renderContent = () => <div>
    <SectionTitle title={'Dates'}/>
    <Row>
      <DatePicker title={'Start Date'}
                  width={'100px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'startDate', value: params.startDate.value}}
                  onChange={(name, value) => {
                    setParams({...params, startDate: {...params.startDate, value: value}});
                  }}
                  maxDate={today}/>
      <DatePicker title={'End Date'}
                  width={'100px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'endDate', value: params.endDate.value}}
                  onChange={(name, value) => {
                    setParams({...params, endDate: {...params.endDate, value: value}});
                  }}
                  maxDate={today}/>
    </Row>
    <SectionTitle title='Advisor Agents'/>
    <Table
      dataManager={agentDataManager}
      columns={[
        {
          title: 'Advisor',
          dataPoint: 'name',
          type: 'text',
          minWidth: 50,
          isSortable: true,
        },
        {
          title: 'Email',
          dataPoint: 'email',
          type: 'text',
          minWidth: 50,
          isSortable: true,
        },
      ]}
      searches={[
        {title: 'Advisor', name: 'name', isFuzzy: true},
        {title: 'Email', name: 'email', isFuzzy: true},
      ]}
      defaultSort={{column: 'name', desc: false}}
      select={{
        type: 'all',
        max: 50,
      }}
      rowIdentifier={'id'}
      height={'200px'}/>
    <SectionTitle title='Investor Users'/>
    <Table
      dataManager={clientDataManager}
      columns={[
        {
          title: 'Household',
          dataPoint: 'name',
          type: 'text',
          minWidth: 50,
          isSortable: true,
        },
        {
          title: 'Email',
          dataPoint: 'email',
          type: 'text',
          minWidth: 50,
          isSortable: true,
        },
      ]}
      searches={[
        {title: 'Household', name: 'name', isFuzzy: true},
        {title: 'Email', name: 'email', isFuzzy: true},
      ]}
      defaultSort={{column: 'name', desc: false}}
      select={{
        type: 'all',
        max: 50,
      }}
      rowIdentifier={'id'}
      height={'200px'}/>
  </div>;

  return <Steps
    height={'100%'}
    completedStateContent={<DescriptiveText>{successMessage || ''}</DescriptiveText>}
    steps={[
      {
        title: 'Advisor View Investor Experience Activity - Select Parameters',
        content: renderContent,
        next: {
          label: 'Generate',
          action: generateReport,
          disabled: !canGenerate(params),
        },
      },
    ].filter(s => !!s)}
    stepIndex={step}
    cancelAction={props.cancelAction}
    canStepBack={false}
    errorMessage={errorMessage}/>;
};

const mapStateToProps = state => ({
  session: state.session,
  unreadReportsTimeoutID: state.badges.unreadReportsTimeoutID,
  unreadReports: state.badges.unreadReports,
  reportPageVisitTimestamp: state.badges.reportPageVisitTimestamp,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getUnreadReports: (timeoutID, unread, timestamp, interval) => dispatch(_getUnreadReports(timeoutID, unread, timestamp, interval)),
});

AdvisorViewInvestorExperienceActivity.propTypes = {
  /**
   * The Report to be generated
   */
  report: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  /**
   * Is from Models Reducer
   */
  openModal: PropTypes.func.isRequired,
  /**
   * To reset the Report generate tile to default state
   */
  cancelAction: PropTypes.func.isRequired,
  /**
   /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      firm: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }),
    }),
  }).isRequired,
  /**
   * Are from badges reducer
   */
  getUnreadReports: PropTypes.func,
  unreadReportsTimeoutID: PropTypes.object,
  unreadReports: PropTypes.object,
  reportPageVisitTimestamp: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdvisorViewInvestorExperienceActivity);
